// src/encounter/structured-note/sign-blanks.ts
//
// What is left before the note can be signed: the template's required items that are still blank, in note order, and the one
// way to go to one. Shared by the Wrap-up's "Before you sign" list and the floating sign bar (`visitNoteV2`, list A9), so the two
// can never list different items.
import type { Questionnaire, QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import type { PaStepKey } from '../lib/encounter-steps';
import { isQuestionnaireStep, questionnaireForStep } from '../lib/questionnaire-groups';
import { answerMap, noteItemAnchor, openRequired } from './note-adapter';
import { focusJumpTarget, scrollJumpTarget } from './jump-focus';

export interface SignBlank {
  step: PaStepKey;
  linkId: string;
  label: string;
}

/** Every required blank of every questionnaire step on the nav, in step order. */
export function signBlanks(questionnaire: Questionnaire | undefined, steps: readonly PaStepKey[], response: QuestionnaireResponse): SignBlank[] {
  if (!questionnaire) return [];
  const values = answerMap(response);
  return steps.flatMap((step) =>
    isQuestionnaireStep(step) ? openRequired(questionnaireForStep(questionnaire, step), values).map((o) => ({ ...o, step })) : [],
  );
}

/** Go to one blank: its step, then (after the section renders) its row, else its block. Only the note's own scroll container
 *  moves, never the document (the independent review of #634, item A), and keyboard focus moves with it (audit FV2-22, WCAG 2.4.3). */
export function goToSignBlank({ step, linkId, label }: SignBlank, onStepChange: (step: PaStepKey) => void): void {
  onStepChange(step);
  setTimeout(() => {
    const target =
      document.getElementById(noteItemAnchor(linkId)) ??
      document.querySelector(`[data-item="${CSS.escape(linkId)}"], [data-system="${CSS.escape(linkId)}"]`) ??
      // An item drawn after its block ("More HPI": a date, an attachment): the run that draws it.
      document.querySelector(`[data-outside-items~="${CSS.escape(linkId)}"]`) ??
      document.getElementById(`note-block-${step}`);
    if (!(target instanceof HTMLElement)) return;
    scrollJumpTarget(target);
    focusJumpTarget(target, label);
  }, 0);
}
